<template>
  <div
    :aria-describedby="ariaDescribedby"
    class="count label-m-medium"
    :class="classes"
    data-pendo-ds="count"
    :style="styles"
    :tabindex="disabled || !focusable || inputFocused ? -1 : 0"
    @blur="focused = false"
    @focus="editable ? focusInput() : focused = focusable"
    @mouseenter="hovered = hoverable"
    @mouseleave="hovered = false"
  >
    <template v-if="!isMounted">
      {{ count }}
    </template>
    <template v-else>
      <transition
        mode="out-in"
        name="fade"
      >
        <input
          v-if="editable && !loading"
          ref="childInput"
          aria-label="Edit count"
          class="count-input label-m-medium"
          :class="size === 'compact' ? 'compact' : ''"
          :disabled="disabled"
          :max="max"
          :placeholder="placeholder"
          :style="{ width: inputWidth + 'ch' }"
          :value="count"
          @blur="handleInputBlur($event)"
          @input="$emit('update:value', $event.target.value)"
          @keydown.enter="handleInputBlur($event)"
          @keydown.esc="handleInputBlur($event)"
        >
        <div
          v-else-if="loading"
          class="loading"
          :class="{compact: size === 'compact', 'count-input': editable}"
        >
          <BaseIcon
            class="loading__icon"
            color="var(--blueberry-1000)"
            icon="fas fa-loader"
            spin
          />
        </div>
        <div
          v-else
          :key="count"
          class="count-text"
          :class="size === 'compact' ? 'compact' : ''"
        >
          <slot name="text">
            {{ displayCount }}
          </slot>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
const SIZES = ['comfortable', 'compact']
import BaseIcon from './BaseIcon.vue'
import { library } from '@fortawesome/fontawesome-svg-core'

import { faLoader } from '@fortawesome/pro-solid-svg-icons'

library.add(faLoader)
export default {
  name: 'BaseCount',
  components: {
    BaseIcon,
  },
  props: {
    count: { type: Number, required: false, default: 0 },
    size: { type: String, required: false, default: 'comfortable', validator: (value) => SIZES.includes(value) },
    fillColor: { type: String, required: false, default: 'var(--blueberry-100)' },
    textColor: { type: String, required: false, default: 'var(--blueberry-1000)' },
    borderColor: { type: String, required: false, default: 'var(--blueberry-300)' },
    hoverColor: { type: String, required: false, default: 'var(--blueberry-200)' },
    focusable: { type: Boolean, required: false, default: true },
    hoverable: { type: Boolean, required: false, default: true },
    fillable: { type: Boolean, required: false, default: false },
    border: { type: Boolean, required: false, default: false },
    toggled: { type: Boolean, required: false, default: false },
    disabled: { type: Boolean, required: false, default: false },
    max: { type: Number, required: false, default: null },
    loading: { type: Boolean, default: false },
    ariaDescribedby: { type: String, required: false, default: '' },
    editable: { type: Boolean, required: false, default: false },
    editing: { type: Boolean, required: false, default: false },
    placeholder: { type: String, required: false, default: '' },
    inputWidth: { type: Number, required: false, default: 1 },
    hasError: { type: Boolean, required: false, default: false },

  },
  emits: ['update:value', 'blur'],
  data() {
    return {
      isMounted: false,
      focused: false,
      inputFocused: false,
      hovered: false,
    }
  },
  computed: {
    classes() {
      return {
        toggled: this.toggled && !this.disabled && !this.loading,
        disabled: this.disabled && !this.loading,
        hover: this.hovered && !this.disabled && !this.loading && !this.toggled && !this.editing && !this.hasError,
        filled: this.fillable && (this.count > 0) && !this.loading && !this.disabled && !this.toggled,
        compact: this.size === 'compact',
        error: this.editable && this.hasError,
        editable: this.editable,
      }
    },
    styles() {
      return {
        borderWidth: this.border || this.editing || this.hasError ? '1px' : '0',
        outlineWidth: this.focused && !this.disabled ? '2px' : '0',
        '--count-text-color': this.editing || this.hasError ? 'var(--pepper-1000)' : this.textColor,
        '--count-fill-color': this.editing || this.hasError ? 'var(--salt)' : this.fillColor,
        '--count-hover-color': this.hoverColor,
        '--count-border-color': this.getBorderColor,
      }
    },
    displayCount() {
      if (this.loading) return ''

      return this.count
    },
    getBorderColor() {
      if (this.hasError) return 'var(--chicago-dog-red-1000)'
      return this.editing ? 'var(--blueberry-1000)' : this.borderColor
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.isMounted = true
    })
  },
  methods: {
    handleInputBlur(event) {
      this.focused = false
      this.inputFocused = false
      this.$refs.childInput.blur()
      this.$emit('blur', event)
    },
    focusInput() {
      if (this.loading || this.disabled) return
      this.$refs.childInput.focus()
      this.inputFocused = true
    },
  },
}
</script>

<style scoped lang='scss'>
.count {
  position: relative;
  border-radius: var(--cr-br-small);
  color: var(--count-text-color);
  background-color: var(--count-fill-color);
  border-color: var(--count-border-color);
  border-style: solid;
  outline-color: var(--blueberry-300);
  outline-style: solid;
  display: inline-block;
  height: 20px;
  padding: 0px 6px;
  box-sizing: content-box;
  align-content: center;
  &.error {
    border-color: var(--chicago-dog-red-1000);
  }
  &.toggled {
    color: var(--count-text-color);
    background-color: var(--salt);
  }

  &.disabled {
    color: var(--pepper-600);
    background-color: var(--pepper-200);
    border-color: var(--pepper-300);
  }

  .loading {
    display: block;
    &__icon {
      display: block;
    }
    &.compact {
      font-size: 10px;
      display: block;
    }
  }

  &.error {
    border-color: var(--chicago-dog-red-1000);
    border-radius: var(--cr-br-small);
  }

  &.hover {
    cursor: pointer;
    background-color: var(--count-hover-color);
    color: var(--count-text-color);
    .count-input {
      background-color: var(--count-hover-color);
      color: var(--count-text-color);
    }
  }

  &.filled {
    background-color: var(--count-text-color);
    color: var(--salt);
  }

  &.compact {
    height: 14px;
    padding: 2px 4px;
  }

  &.editable {
    padding: 0px var(--space-1_5);
    align-content: center;
    height: 22px;

    &.compact {
      height: 16px;
      padding: 0px 3px;
    }
  }
}

.count-text {
  line-height: 20px;

  &.compact {
    line-height: 14px;
    font-size: 10px;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.count-input {
  border: none;
  outline: none;
  text-align: center;
  color: var(--count-text-color);
  background-color: var(--count-fill-color);
  box-sizing: content-box;
  align-content: center;
  padding: 0;
  display: inline-block;
  align-items: center;
  justify-content: center;
  line-height: normal;
  height: 22px;
  vertical-align: middle;

  &::placeholder {
    color: var(--pepper-200);
  }

    &.hover {
    background-color: var(--count-hover-color);
    color: var(--count-text-color);
  }

  &.filled {
    background-color: var(--count-text-color);
    color: var(--salt);
  }
  &.compact {
    font-size: 10px;
    padding: 0;
    line-height: 11px;
    display: block;
  }

  &:disabled {
    background-color: var(--pepper-200);
    color: var(--pepper-600);
  }
}
</style>
